import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

const BookReviewContainer = styled.div`
  padding: 6rem 1.5rem 5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  background: rgb(var(--bg));
  min-height: 100vh;
  font-family: 'Arial', sans-serif;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 10% 0%, rgba(212, 165, 40, 0.12), transparent 40%);
    pointer-events: none;
  }

  & > * {
    position: relative;
    z-index: 1;
  }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: #d4a528;
  text-align: center;
  margin-bottom: 1rem;
  line-height: 1.2;
`;

const Author = styled.p`
  font-size: 1.2rem;
  color: rgb(var(--fg) / 0.78);
  margin-bottom: 1rem;
  font-style: italic;
`;

const CoverImage = styled.img`
  width: 100%;
  max-width: 300px;
  max-height: 600px;
  border-radius: 8px;
  margin-bottom: 1rem;
  box-shadow: 0 20px 40px rgb(0 0 0 / calc(0.5 * var(--shadow-mul)));
  border: 1px solid rgb(var(--fg) / 0.08);
`;

const DatePublished = styled.p`
  font-size: 1rem;
  color: rgb(var(--gray) / 0.55);
  margin-bottom: 2rem;
`;

const ContentSection = styled.div`
  width: 100%;
  max-width: 1200px;
  margin-bottom: 2rem;
  background: rgb(var(--fg) / 0.035);
  border: 1px solid rgb(var(--fg) / 0.06);
  padding: 2rem;
  border-radius: 20px;
  backdrop-filter: blur(24px) saturate(1.15);
  -webkit-backdrop-filter: blur(24px) saturate(1.15);
  box-shadow: 0 24px 56px -16px rgb(0 0 0 / calc(0.5 * var(--shadow-mul)));
`;

const SectionTitle = styled.h2`
  font-size: 1.8rem;
  margin-bottom: 1rem;
  color: #d4a528;
  border-bottom: 1px solid rgba(212, 165, 40, 0.3);
  padding-bottom: 0.5rem;
`;

const SectionContent = styled.div`
  font-size: 1rem;
  line-height: 1.8;
  color: rgb(var(--fg) / 0.82);
`;

const Quote = styled.blockquote`
  font-style: italic;
  margin: 1.5rem 0;
  padding: 1rem;
  background: rgb(var(--fg) / 0.04);
  border-left: 3px solid #d4a528;
  border-radius: 8px;
  color: rgb(var(--fg) / 0.78);
`;

const ListItem = styled.li`
  margin-bottom: 1rem;
  font-size: 1rem;
  color: rgb(var(--fg) / 0.82);
`;

const OrderedList = styled.ol`
  padding-left: 1rem;
  color: rgb(var(--fg) / 0.82);
`;

const TagContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  max-width: 1200px;
  gap: 0.5rem;
  margin-top: 1.5rem;
`;

const Tag = styled.span`
  background: rgb(var(--fg) / 0.05);
  border: 1px solid rgba(212, 165, 40, 0.3);
  color: rgb(var(--fg) / 0.9);
  padding: 0.4rem 0.7rem;
  border-radius: 12px;
  font-size: 0.8rem;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
`;

const Button = styled.button`
  display: inline-block;
  margin: 2rem 0;
  padding: 0.9rem 2rem;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgb(var(--fg) / 0.92);
  background: rgb(var(--fg) / 0.06);
  border: 1px solid rgb(var(--fg) / 0.14);
  border-radius: 999px;
  cursor: pointer;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  transition: transform 0.25s, border-color 0.25s, background 0.25s;
  text-align: center;

  &:hover {
    transform: translateY(-1px);
    background: rgb(var(--fg) / 0.1);
    border-color: rgba(212, 165, 40, 0.5);
  }
`;

export const ChipWarMetadata = {
  title: "Chip War",
  description: "The Fight for the World's Most Critical Technology",
  tags: ['Technology', 'Geopolitics', 'Supply Chain', 'Innovation', 'Business'],
  author: "Chris Miller",
  datePublished: "2023-09-30",
  category: "Book Review",
  path: "/book-review/chip-war",
  coverImage: "/images/chip_war.jpg", // Ensure this path is correct
};

const ChipWar = () => {
  const navigate = useNavigate();
  const metadata = ChipWarMetadata;

  return (
    <BookReviewContainer>
      <Title>{metadata.title}</Title>
      <Author>by {metadata.author}</Author>
      <CoverImage src={metadata.coverImage} alt={metadata.title} />
      <DatePublished>Date Published: {metadata.datePublished}</DatePublished>
      <TagContainer>
          {metadata.tags.map((tag, index) => (
            <Tag key={index}>{tag}</Tag>
          ))}
      </TagContainer>
      <br />
      <br />
      <ContentSection>
        <SectionTitle>Summary</SectionTitle>
        <SectionContent>
          <p>
            "Chip War" delves into the global competition surrounding semiconductors, the technology powering
            the modern world. Chris Miller takes readers on a journey through the history, development, and strategic
            importance of microchips, highlighting their impact on the global economy and geopolitics.
          </p>
          <br />
          <p>
            The book examines how nations, especially the United States and China, have fought to dominate
            semiconductor manufacturing and innovation. Miller explores the critical role these technologies play in
            everything from smartphones to military equipment and emphasizes the challenges in securing supply chains.
          </p>
          <br />
          <p>
            "Chip War" not only offers an in-depth analysis of the industry but also discusses the consequences
            of technological dominance and how it shapes the geopolitical landscape. With engaging narratives and insights,
            Miller provides a clear perspective on the stakes of the chip race.
          </p>
        </SectionContent>
      </ContentSection>

      <ContentSection>
        <SectionTitle>Key Themes or Concepts</SectionTitle>
        <SectionContent>
          <OrderedList>
            <ListItem><strong>Semiconductor Dominance:</strong> The competition between nations for control over chip technology and production.</ListItem>
            <ListItem><strong>Geopolitical Impact:</strong> The strategic importance of microchips in global politics and military power.</ListItem>
            <ListItem><strong>Supply Chain Vulnerabilities:</strong> The challenges of securing supply chains in a globally interconnected market.</ListItem>
            <ListItem><strong>Innovation and Market Forces:</strong> How companies drive innovation in a highly competitive sector.</ListItem>
            <ListItem><strong>US-China Rivalry:</strong> The growing tensions between the US and China over technological supremacy.</ListItem>
            <ListItem><strong>Future of Technology:</strong> The potential paths and developments in the semiconductor industry and their implications for the future.</ListItem>
          </OrderedList>
        </SectionContent>
      </ContentSection>

      <ContentSection>
        <SectionTitle>Favorite Quotes</SectionTitle>
        <SectionContent>
          <Quote>
            "The future of technology will be defined by those who control the chips."
          </Quote>
          <Quote>
            "In the battle for technological dominance, microchips have become the new oil."
          </Quote>
        </SectionContent>
      </ContentSection>

      {/* Personal Reflection Section */}
      <ContentSection>
        <SectionTitle>Personal Reflection</SectionTitle>
        <SectionContent>
          <p>
            "Chip War" opened my eyes to just how critical semiconductors are in shaping the modern world, not only from a technological perspective but also in terms of global politics and power dynamics. Before reading this, I had an understanding of the importance of microchips in everyday devices like phones and computers, but I hadn’t fully grasped their pivotal role in geopolitics.
          </p>
          <br />
          <p>
            One of the key takeaways for me was the fragile nature of the global supply chain. The book highlights how countries like the United States and China are fighting for dominance, not just in production but also in controlling the innovation behind semiconductors. The fact that this competition extends beyond economics into military applications really emphasizes how intertwined technology and national security have become.
          </p>
          <br />
          <p>
            What resonated most was the comparison of microchips to oil—a resource that nations will continue to fight over because it drives so much of modern life. It made me rethink the future of technology and how much innovation depends on access to these critical resources. It’s clear that whoever controls the future of semiconductors controls much more than just the tech industry; they control the future of global power itself.
          </p>
          <br />
          <p>
            This book left me with a deeper appreciation for the complexity of global competition and innovation. As someone who is interested in the intersection of technology and business, "Chip War" solidified my understanding of why advancements in technology aren’t just about convenience or progress—they’re about maintaining competitive advantage on a global scale.
          </p>
        </SectionContent>
      </ContentSection>

      <Button onClick={() => navigate('/blog')}>Back to Blog</Button>
    </BookReviewContainer>
  );
};

export default ChipWar;